import { createSlice } from '@reduxjs/toolkit';

const initialState = {
	orgSettings: {},
	personSettings: {},

	mergedOrgSettings: {},
	mergedPersonSettings: {},

	orgSettingsLoading: false,
	personSettingsLoading: false,
	orgSettingsLoadingComplete: false,
	personSettingsLoadingComplete: false,

	// SWR cache timestamps keyed by orgID / personID — null = never loaded
	orgSettingsTimeStamp: {},
	personSettingsTimeStamp: {},

	selectedSettingGroupID: false,
	error: null,

	upsertOrgSettingsLoading: false,
	// defaultSettingID → true while that row's save is in flight.
	loadingOrgSettingIDs: {},
	upsertError: null,
	// orgID → { defaultSettingID → latest audit row }; an org is absent until an audit viewer loads it.
	orgSettingsLastChanged: {}
};

const settingsSlice = createSlice({
	name: 'settings',
	initialState,
	reducers: {
		// ── Load (existing) ────────────────────────────────────────────────
		loadOrgSettings: (state) => {
			state.orgSettingsLoading = true;
			state.error = null;
		},
		loadOrgSettingsSuccess: (state, action) => {
			state.orgSettings[action.payload.orgID] = action.payload.settings;
			state.orgSettingsLoading = false;
			state.orgSettingsLoadingComplete = true;
			state.orgSettingsTimeStamp[action.payload.orgID] = Date.now();
		},
		loadOrgSettingsError: (state, action) => {
			state.orgSettingsLoading = false;
			state.error = action.payload;
		},
		loadPersonSettings: (state) => {
			state.personSettingsLoading = true;
			state.error = null;
		},
		loadPersonSettingsSuccess: (state, action) => {
			state.personSettings[action.payload.personID] = action.payload.settings;
			state.personSettingsLoading = false;
			state.personSettingsLoadingComplete = true;
			state.personSettingsTimeStamp[action.payload.personID] = Date.now();
		},
		loadPersonSettingsError: (state, action) => {
			state.personSettingsLoading = false;
			state.error = action.payload;
		},
		// Rows still saving keep their optimistic value until their own save settles.
		mergeOrgSettings: (state, action) => {
			const { orgID, mergedSettings } = action.payload;
			const current = state.mergedOrgSettings[orgID] || [];
			state.mergedOrgSettings[orgID] = (mergedSettings || []).map((row) => {
				if (!state.loadingOrgSettingIDs[row.defaultSettingID]) return row;
				const pending = current.find((s) => s.defaultSettingID === row.defaultSettingID);
				return pending ? { ...row, value: pending.value } : row;
			});
		},

		// Patches one org setting in merged and raw state: live socket updates (ORG_SETTING_UPDATED)
		// and the optimistic value while a row saves.
		updateOrgSettingValue: (state, action) => {
			const { orgID, defaultSettingID, value } = action.payload;
			const patch = (arr) => {
				if (!Array.isArray(arr)) return;
				const s = arr.find((x) => x.defaultSettingID === defaultSettingID);
				if (s) s.value = value;
			};
			patch(state.mergedOrgSettings[orgID]);
			patch(state.orgSettings[orgID]);
		},
		mergePersonSettings: (state, action) => {
			state.mergedPersonSettings[action.payload.personID] = action.payload.mergedSettings;
		},

		upsertOrgSettings: (state, action) => {
			state.upsertOrgSettingsLoading = true;
			state.upsertError = null;
			(action.payload?.settings || []).forEach(({ defaultSettingID }) => {
				state.loadingOrgSettingIDs[defaultSettingID] = true;
			});
		},
		upsertOrgSettingsSuccess: (state, action) => {
			state.upsertOrgSettingsLoading = false;
			(action.payload?.defaultSettingIDs || []).forEach((id) => delete state.loadingOrgSettingIDs[id]);
		},
		upsertOrgSettingsError: (state, action) => {
			state.upsertOrgSettingsLoading = false;
			state.upsertError = action.payload?.error;
			(action.payload?.defaultSettingIDs || []).forEach((id) => delete state.loadingOrgSettingIDs[id]);
		},

		loadOrgSettingsLastChanged: () => {},
		// Replaces the requested ids, so a setting with no row in this org drops the old one.
		loadOrgSettingsLastChangedSuccess: (state, action) => {
			const { orgID, defaultSettingIDs = [], rows = [] } = action.payload || {};
			const byID = { ...state.orgSettingsLastChanged[orgID] };
			defaultSettingIDs.forEach((id) => delete byID[id]);
			rows.forEach((row) => {
				byID[Number(row.defaultSettingID)] = row;
			});
			state.orgSettingsLastChanged[orgID] = byID;
		},

		// ── Person setting (legacy — existing) ─────────────────────────────
		updatePersonSetting: () => {},
		updatePersonSettingError: (state, action) => {
			state.error = action.payload;
		},
		// ! Deprecated. DO NOT USE THIS ACTION ANYMORE. Use setSelectedSettingGroup instead.
		updateSelectedSettingGroup(state, action) {
			state.selectedSettingGroupID = action.payload;
		},
		setSelectedSettingGroup(state, action) {
			state.selectedSettingGroupID = action.payload;
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
	}
});

export const settingsActions = settingsSlice.actions;
export default settingsSlice.reducer;
